const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const parseTimestamp = (value) => {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const normalizeElapsed = (value) =>
  Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0;

export const formatTimelineOffset = (elapsedMilliseconds) => {
  const elapsed = normalizeElapsed(elapsedMilliseconds);

  if (elapsed < 1000) {
    return `+${Math.round(elapsed)}ms`;
  }

  return `+${(elapsed / 1000).toFixed(3)}s`;
};

export const formatCoverage = (measuredMilliseconds, totalMilliseconds) => {
  const total = normalizeElapsed(totalMilliseconds);

  if (total <= 0) {
    return "0%";
  }

  const measured = normalizeElapsed(measuredMilliseconds);
  const coverage = Math.min(measured / total, 1) * 100;
  return `${Math.round(coverage)}%`;
};

export const getEventStatus = (event = {}) => {
  if (event.error || event.ok === false) {
    return "error";
  }

  const status = Number(event.status);
  if (Number.isFinite(status) && status >= 400) {
    return "error";
  }

  if (
    event.ok === true ||
    (Number.isFinite(status) && status >= 200 && status < 400)
  ) {
    return "success";
  }

  return "unknown";
};

export const getEventStatusLabel = (event = {}) => {
  const status = Number(event.status);
  const eventStatus = getEventStatus(event);

  if (Number.isFinite(status)) {
    return `${status} ${eventStatus}`;
  }

  if (eventStatus === "error") {
    return "Error";
  }

  if (eventStatus === "success") {
    return "Success";
  }

  return "Unknown";
};

const findOrigin = (events, startedAt) => {
  const startedAtTimestamp = parseTimestamp(startedAt);
  if (startedAtTimestamp !== null) {
    return startedAtTimestamp;
  }

  return events.reduce((earliestStart, event) => {
    const eventStart = parseTimestamp(event?.start);
    if (eventStart === null) {
      return earliestStart;
    }

    return earliestStart === null
      ? eventStart
      : Math.min(earliestStart, eventStart);
  }, null);
};

const mergeIntervals = (intervals) => {
  const sorted = intervals
    .slice()
    .sort((left, right) => left.startOffset - right.startOffset);
  const merged = intervals.slice(0, 0);

  for (const interval of sorted) {
    const previous = merged[merged.length - 1];

    if (!previous || interval.startOffset > previous.endOffset) {
      merged.push({ ...interval });
      continue;
    }

    previous.endOffset = Math.max(previous.endOffset, interval.endOffset);
  }

  return merged;
};

const createGap = (index, startOffset, endOffset) => ({
  id: `gap-${index}`,
  label: "Unmeasured",
  startOffset,
  endOffset,
  elapsed: endOffset - startOffset,
  startLabel: formatTimelineOffset(startOffset),
  endLabel: formatTimelineOffset(endOffset)
});

const createGaps = (intervals, totalElapsedMilliseconds) => {
  const gaps = intervals.slice(0, 0);
  let cursor = 0;

  for (const interval of intervals) {
    const startOffset = Math.max(interval.startOffset, cursor);

    if (startOffset > cursor) {
      gaps.push(createGap(gaps.length, cursor, startOffset));
    }

    cursor = Math.max(cursor, interval.endOffset);
  }

  if (cursor < totalElapsedMilliseconds) {
    gaps.push(createGap(gaps.length, cursor, totalElapsedMilliseconds));
  }

  return gaps;
};

const sumIntervals = (intervals) =>
  intervals.reduce(
    (totalElapsed, interval) =>
      totalElapsed + (interval.endOffset - interval.startOffset),
    0
  );

export const createProfilingTimeline = ({
  events = [],
  totalElapsedMilliseconds = 0,
  startedAt
} = {}) => {
  const total = normalizeElapsed(totalElapsedMilliseconds);
  const safeEvents = Array.isArray(events) ? events : [];
  const origin = findOrigin(safeEvents, startedAt);
  const eventElapsedMilliseconds = safeEvents.reduce(
    (totalMeasured, event) => totalMeasured + normalizeElapsed(event?.elapsed),
    0
  );

  const rows = safeEvents.map((event, index) => {
    const elapsed = normalizeElapsed(event?.elapsed);
    const startTimelineMs = normalizeElapsed(event?.startTimelineMs);
    const endTimelineMs = normalizeElapsed(event?.endTimelineMs);
    const hasMonotonicOffset =
      Number.isFinite(Number(event?.startTimelineMs)) &&
      Number.isFinite(Number(event?.endTimelineMs)) &&
      endTimelineMs >= startTimelineMs;
    const startTimestamp = parseTimestamp(event?.start);
    const endTimestamp = parseTimestamp(event?.end);
    const hasTimestampOffset =
      origin !== null && startTimestamp !== null && endTimestamp !== null;
    const startOffset = hasMonotonicOffset
      ? clamp(startTimelineMs, 0, total)
      : hasTimestampOffset
        ? clamp(startTimestamp - origin, 0, total)
        : 0;
    const endOffset = hasMonotonicOffset
      ? clamp(endTimelineMs, 0, total)
      : hasTimestampOffset
        ? clamp(endTimestamp - origin, 0, total)
        : clamp(elapsed, 0, total);

    return {
      id: `event-${index}`,
      event,
      action: event?.action,
      label: event?.label || event?.action || `Event ${index + 1}`,
      status: getEventStatus(event),
      statusLabel: getEventStatusLabel(event),
      elapsed,
      startOffset,
      endOffset: Math.max(startOffset, endOffset),
      startLabel: formatTimelineOffset(startOffset),
      endLabel: formatTimelineOffset(Math.max(startOffset, endOffset)),
      hasReliableOffset: hasMonotonicOffset || hasTimestampOffset
    };
  });

  const hasReliableOffsets = rows.every((row) => row.hasReliableOffset);
  const reliableIntervals = rows
    .filter((row) => row.hasReliableOffset && row.endOffset > row.startOffset)
    .map((row) => ({
      startOffset: row.startOffset,
      endOffset: row.endOffset
    }));
  const mergedIntervals = mergeIntervals(reliableIntervals);
  const measuredMilliseconds = hasReliableOffsets
    ? sumIntervals(mergedIntervals)
    : clamp(eventElapsedMilliseconds, 0, total);

  const gaps = hasReliableOffsets
    ? createGaps(mergedIntervals, total)
    : rows.slice(0, 0);

  if (!hasReliableOffsets && total > 0) {
    gaps.push({
      id: "gap-unknown",
      label: "Unmeasured timing unknown",
      startOffset: 0,
      endOffset: total,
      elapsed: total
    });
  }

  return {
    rows,
    gaps,
    totalElapsedMilliseconds: total,
    eventElapsedMilliseconds,
    measuredMilliseconds,
    unmeasuredMilliseconds: Math.max(total - measuredMilliseconds, 0),
    coverageLabel: formatCoverage(measuredMilliseconds, total),
    hasReliableOffsets
  };
};
